import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Composer } from "@/components/Composer";
import { PostCard } from "@/components/PostCard";
import { UserAvatar } from "@/components/UserAvatar";
import { GroupFormDialog } from "@/pages/GroupsPage";

const MembersStrip = ({ members }) => (
  <div className="hb-card p-5" data-testid="group-members">
    <div className="label-accent mb-3">Members</div>
    <div className="flex flex-wrap gap-2">
      {members.map((m) => (
        <Link key={m.id} to={`/members/${m.id}`} title={m.name} data-testid={`group-member-${m.id}`}>
          <UserAvatar user={m} size={36} />
        </Link>
      ))}
    </div>
  </div>
);

export default function GroupPage() {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const [g, setG] = useState(null);
  const [posts, setPosts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const loadGroup = () => api.get(`/groups/${groupId}`).then((r) => {
    setG(r.data);
    if (r.data.joined || isAdmin) {
      api.get("/posts", { params: { group: groupId } }).then((p) => setPosts(p.data));
    } else {
      setPosts(null);
    }
  }).catch(() => navigate("/groups"));

  useEffect(() => {
    loadGroup();
    api.get("/categories").then((r) => setCategories(r.data));
  }, [groupId]);

  const toggleJoin = async () => {
    try {
      await api.post(`/groups/${groupId}/${g.joined ? "leave" : "join"}`);
      toast.success(g.joined ? "You left the group" : `Welcome to ${g.name}!`);
      loadGroup();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this group and all its posts?")) return;
    try {
      await api.delete(`/groups/${groupId}`);
      navigate("/groups");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!g) return <div className="text-[var(--hb-muted)]">Loading group...</div>;
  const canSee = g.joined || isAdmin;
  const update = (p) => setPosts(posts.map((x) => (x.id === p.id ? p : x)));

  return (
    <div data-testid="group-page">
      <Link to="/groups" className="inline-flex items-center gap-2 text-sm text-[var(--hb-muted)] hover:text-[var(--hb-ink)]" data-testid="group-back-link">
        <ArrowLeft size={15} /> Groups
      </Link>
      <div className="relative mt-4 rounded-2xl overflow-hidden bg-[var(--hb-ink)] text-white grain">
        {g.cover_url && <img src={imgSrc(g.cover_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <div className="relative p-6 md:p-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl" data-testid="group-name">{g.name}</h1>
            {g.description && <p className="text-white/80 mt-2 max-w-xl">{g.description}</p>}
            <div className="flex items-center gap-1.5 mt-3 text-sm text-white/70"><Users size={14} /> {g.member_count} member{g.member_count === 1 ? "" : "s"}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className={g.joined ? "btn-ghost" : "btn-primary"} onClick={toggleJoin} data-testid="group-join-toggle">
              {g.joined ? "Leave group" : "Join group"}
            </button>
            {isAdmin && <button className="btn-ghost" onClick={() => setEditOpen(true)} data-testid="group-edit-button"><Pencil size={13} /></button>}
            {isAdmin && <button className="btn-ghost" onClick={remove} data-testid="group-delete-button"><Trash2 size={13} /></button>}
          </div>
        </div>
      </div>
      {!canSee ? (
        <div className="hb-card p-10 text-center mt-6" data-testid="group-locked">
          <Lock className="mx-auto" size={22} />
          <p className="font-display text-xl mt-3">Join to see the conversation</p>
          <p className="text-sm text-[var(--hb-muted)] mt-1">Discussions in this group are only visible to its members.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_300px] gap-8 mt-6">
          <section className="space-y-5 min-w-0">
            <Composer categories={categories} groupId={g.id} onPosted={(p) => setPosts([p, ...(posts || [])])} />
            {posts?.length === 0 && <div className="hb-card p-10 text-center text-[var(--hb-muted)]" data-testid="group-feed-empty">No posts yet. Start the conversation!</div>}
            {posts?.map((p) => (
              <PostCard key={p.id} post={p} onChange={update} onDelete={(id) => setPosts(posts.filter((x) => x.id !== id))} />
            ))}
          </section>
          <aside><MembersStrip members={g.members_preview} /></aside>
        </div>
      )}
      {isAdmin && (
        <GroupFormDialog open={editOpen} setOpen={setEditOpen}
          initial={{ id: g.id, name: g.name, description: g.description, cover_url: g.cover_url }}
          onSaved={() => loadGroup()} />
      )}
    </div>
  );
}
